import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Copy,
  Eye,
  EyeOff,
  RotateCw,
  Trash2,
  Plus,
  Users,
  ChevronUp,
  ChevronDown,
  Sheet as SheetIcon,
  Download
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  fetchJson,
  type Settings as SettingsT,
  type Cadence,
  type Questionnaire,
  type Question,
  type EntryMenu,
  type MenuItem,
  type MenuItemType,
  type AfterAction,
  type GoogleConnectionStatus
} from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';

const MENU_TYPE_LABELS: Record<MenuItemType, string> = {
  questionnaire: 'שאלון',
  extension: 'שלוחה במערכת',
  hangup: 'ניתוק',
  main: 'שלוחה ראשית'
};

const CADENCE_OPTIONS: { value: Cadence; label: string; desc: string }[] = [
  { value: 'manual', label: 'ידני', desc: 'אדמין פותח/סוגר מחזורים בעמוד "מחזורים"' },
  { value: 'daily', label: 'יומי', desc: 'מחזור חדש כל יום אוטומטית' },
  { value: 'weekly', label: 'שבועי', desc: 'מחזור חדש כל תחילת שבוע אוטומטית' },
  { value: 'monthly', label: 'חודשי (לועזי)', desc: 'מחזור חדש בכל א\' לחודש לועזי אוטומטית' },
  { value: 'hebrew_monthly', label: 'חודשי (עברי)', desc: 'מחזור חדש בכל ראש חודש עברי אוטומטית' },
  { value: 'unlimited', label: 'ללא הגבלה', desc: 'אין מחזורים. כל שיחה יוצרת רשומה חדשה' }
];

function buildYemotApiConfig(webhookUrl: string): string {
  return [
    'type=api',
    'title=Easy Call',
    `api_link=${webhookUrl}`,
    'api_url_post=yes',
    'api_end_goto=/',
    'verify_phone_operator_network=yes'
  ].join('\n');
}

interface OrgAdmin {
  email: string;
  addedAt: string;
  addedBy: string | null;
}

function OrgAdminsCard() {
  const confirm = useConfirm();
  const [admins, setAdmins] = useState<OrgAdmin[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchJson<OrgAdmin[]>('/api/org/admins');
      setAdmins(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function addAdmin() {
    const email = newEmail.trim();
    if (!email.includes('@')) return;
    setBusy(true);
    try {
      await fetchJson('/api/org/admins', { method: 'POST', body: JSON.stringify({ email }) });
      toast.success('נוסף');
      setNewEmail('');
      refresh();
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(e.message === 'invalid_email' ? 'כתובת מייל לא תקינה' : e.message || 'שגיאה');
    } finally {
      setBusy(false);
    }
  }

  async function removeAdmin(email: string) {
    const ok = await confirm({
      title: `להסיר את ${email}?`,
      description: 'משתמש זה לא יוכל יותר להתחבר לארגון.',
      confirmText: 'הסר',
      variant: 'destructive'
    });
    if (!ok) return;
    try {
      await fetchJson(`/api/org/admins/${encodeURIComponent(email)}`, { method: 'DELETE' });
      toast.success('הוסר');
      refresh();
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(
        e.message === 'last_admin'
          ? 'לא ניתן להסיר את המנהל היחיד. הוסף תחילה כתובת נוספת.'
          : e.message || 'שגיאה'
      );
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          כתובות מייל להתחברות
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          מי שיכול להתחבר ולנהל את הארגון. ניתן להוסיף כמה כתובות Google לאותו ארגון.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            placeholder="email@example.com"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            dir="ltr"
            className="sm:max-w-md"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addAdmin();
              }
            }}
          />
          <Button onClick={addAdmin} disabled={!newEmail.includes('@') || busy}>
            <Plus className="h-4 w-4" />
            הוסף
          </Button>
        </div>
        {loading ? (
          <Skeleton className="h-16" />
        ) : admins.length === 0 ? (
          <p className="text-sm text-muted-foreground">אין כתובות מוגדרות.</p>
        ) : (
          <div className="space-y-2">
            {admins.map((a) => (
              <div
                key={a.email}
                className="flex items-center justify-between gap-2 rounded-md border p-2 ps-3"
              >
                <span className="truncate font-mono text-sm" dir="ltr">
                  {a.email}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  onClick={() => removeAdmin(a.email)}
                  aria-label={`הסר ${a.email}`}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DownloadDataCard() {
  const [busy, setBusy] = useState(false);

  async function download() {
    setBusy(true);
    try {
      const res = await fetch('/api/me/org-backup.zip', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('download_failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : 'backup.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success('הנתונים הגולמיים הורדו');
    } catch {
      toast.error('שגיאה בהורדת הנתונים');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          הורדת נתונים גולמיים
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          גיבוי מלא של הארגון בקובץ ZIP - הלקוחות, השאלונים, המחזורים וכל הרשומות בקובצי אקסל.
        </p>
        <Button variant="outline" onClick={download} disabled={busy}>
          <Download className="h-4 w-4" />
          {busy ? 'מכין הורדה...' : 'הורדת גיבוי (ZIP)'}
        </Button>
      </CardContent>
    </Card>
  );
}

function GoogleSheetsCard() {
  const confirm = useConfirm();
  const [status, setStatus] = useState<GoogleConnectionStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const s = await fetchJson<GoogleConnectionStatus>('/api/google/status');
    setStatus(s);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Surface the OAuth redirect outcome (?google=...) once, then clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get('google');
    if (!g) return;
    if (g === 'connected') toast.success('חשבון Google חובר בהצלחה');
    else if (g === 'denied') toast.error('החיבור בוטל');
    else if (g === 'no_refresh')
      toast.error('לא התקבל אישור הרשאה - נסו שוב ואשרו את הגישה ל-Google Drive');
    else if (g === 'session')
      toast.error('פג תוקף החיבור באמצע התהליך - נסו שוב מאותו דפדפן, ואל תפתחו את גוגל בכרטיסייה נפרדת');
    else toast.error('שגיאה בחיבור חשבון Google');
    params.delete('google');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    refresh();
  }, [refresh]);

  async function disconnect() {
    const ok = await confirm({
      title: 'לנתק את חשבון Google?',
      description:
        'קבצי ה-Sheets הקיימים יישארו ב-Drive של החשבון אך יפסיקו להתעדכן אוטומטית. ניתן לחבר מחדש בכל עת.',
      confirmText: 'נתק',
      variant: 'destructive'
    });
    if (!ok) return;
    setBusy(true);
    try {
      await fetchJson('/api/google/disconnect', { method: 'POST' });
      toast.success('נותק');
      refresh();
    } finally {
      setBusy(false);
    }
  }

  function connect() {
    window.location.href = '/auth/google/drive';
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <SheetIcon className="h-5 w-5" />
          חיבור Google Sheets
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          חברו חשבון Google כדי שנתוני השאלונים יסונכרנו אוטומטית לקבצי Google Sheets ייעודיים.
          הקבצים נוצרים ב-Drive של החשבון המחובר (האחסון נספר מול אותו חשבון), ומשותפים אוטומטית
          לכל מנהלי הארגון. את הסנכרון מפעילים לכל שאלון בנפרד, בעמוד עריכת השאלון.
        </p>
        {status?.connected ? (
          <>
            <div className="flex items-center gap-2 rounded-md border p-2 ps-3">
              <Badge variant="success">מחובר</Badge>
              <span dir="ltr" className="truncate font-mono text-sm">
                {status.ownerEmail}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={connect}>
                חבר חשבון אחר
              </Button>
              <Button variant="outline" size="sm" disabled={busy} onClick={disconnect}>
                נתק
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              החלפת חשבון לא מעבירה קבצים קיימים - הם יישארו ב-Drive של החשבון הקודם.
            </p>
          </>
        ) : (
          <Button onClick={connect}>חבר חשבון Google</Button>
        )}
      </CardContent>
    </Card>
  );
}

function EntryMenuEditor({
  value,
  questionnaires,
  onChange
}: {
  value: EntryMenu | null;
  questionnaires: Questionnaire[];
  onChange: (menu: EntryMenu | null) => void;
}) {
  const enabled = !!value;
  const items = value?.items ?? [];

  function setItems(next: MenuItem[]) {
    onChange({ intro: value?.intro ?? '', items: next });
  }

  function updateItem(idx: number, patch: Partial<MenuItem>) {
    setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function move(idx: number, dir: -1 | 1) {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>תפריט שלוחה</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          תפריט הפניות בכניסה לשיחה. כשמופעל, הוא מחליף את בורר השאלונים האוטומטי - כל מקש מפנה לשאלון
          או לשלוחה במערכת. כך ניתן להשתמש בתוכנה כשלוחת תפריט.
        </p>
        <div className="flex items-center gap-2">
          <Switch
            id="entry_menu_enabled"
            checked={enabled}
            onCheckedChange={(on) => onChange(on ? { intro: '', items: [] } : null)}
          />
          <Label htmlFor="entry_menu_enabled">הפעל תפריט מותאם</Label>
        </div>

        {enabled && (
          <>
            <div className="space-y-1">
              <Label>הודעת פתיחה (אופציונלי)</Label>
              <Textarea
                value={value?.intro ?? ''}
                onChange={(e) => onChange({ intro: e.target.value, items })}
                rows={2}
                placeholder='אם יישאר ריק, המערכת תבנה הודעה אוטומטית: "ל&lt;תיאור&gt; הקש 1, ל&lt;תיאור&gt; הקש 2"'
              />
            </div>

            <div className="space-y-3">
              {items.length === 0 && (
                <p className="text-sm text-muted-foreground">אין פריטים. הוסף פריט להתחלה.</p>
              )}
              {items.map((it, idx) => (
                <div key={idx} className="rounded-md border p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">מקש {idx + 1}</span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={idx === 0}
                        onClick={() => move(idx, -1)}
                        aria-label="העלה"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={idx === items.length - 1}
                        onClick={() => move(idx, 1)}
                        aria-label="הורד"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setItems(items.filter((_, i) => i !== idx))}
                        aria-label="הסר"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label>סוג</Label>
                      <Select
                        value={it.type}
                        onValueChange={(v) =>
                          updateItem(idx, {
                            type: v as MenuItemType,
                            questionnaireId: undefined,
                            extension: undefined
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(MENU_TYPE_LABELS) as MenuItemType[]).map((t) => (
                            <SelectItem key={t} value={t}>
                              {MENU_TYPE_LABELS[t]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {it.type === 'questionnaire' ? (
                      <div className="space-y-1">
                        <Label>שאלון</Label>
                        <Select
                          value={it.questionnaireId || ''}
                          onValueChange={(v) => updateItem(idx, { questionnaireId: v })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="בחר שאלון" />
                          </SelectTrigger>
                          <SelectContent>
                            {questionnaires.map((q) => (
                              <SelectItem key={q.id} value={q.id}>
                                {q.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : it.type === 'extension' ? (
                      <div className="space-y-1">
                        <Label>מספר שלוחה</Label>
                        <Input
                          value={it.extension || ''}
                          onChange={(e) => updateItem(idx, { extension: e.target.value })}
                          dir="ltr"
                          placeholder="למשל 5 או 1/2"
                        />
                      </div>
                    ) : (
                      <div />
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label>
                      תיאור {it.type === 'questionnaire' ? '(אופציונלי)' : '(לטקסט האוטומטי)'}
                    </Label>
                    <Input
                      value={it.label || ''}
                      onChange={(e) => updateItem(idx, { label: e.target.value })}
                      placeholder={
                        it.type === 'questionnaire'
                          ? 'ברירת מחדל: שם השאלון להשמעה'
                          : 'למשל: דקה מזכירה'
                      }
                    />
                  </div>
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setItems([...items, { type: 'questionnaire', label: '' }])}
            >
              <Plus className="h-4 w-4" />
              הוסף פריט
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const confirm = useConfirm();
  const [s, setS] = useState<SettingsT | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [tokenValue, setTokenValue] = useState<string | null>(null);
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [sd, qns] = await Promise.all([
        fetchJson<SettingsT>('/api/settings'),
        fetchJson<Questionnaire[]>('/api/questionnaires')
      ]);
      setS(sd);
      setQuestionnaires(qns);
      const sumQnId = sd.dashboard_sum_question?.questionnaireId;
      if (sumQnId) {
        const qs = await fetchJson<Question[]>(`/api/questionnaires/${sumQnId}/questions`);
        setQuestions(qs);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading || !s) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  function update<K extends keyof SettingsT>(key: K, value: SettingsT[K]) {
    setS((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function save() {
    if (!s) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        cadence: s.cadence,
        allow_update_within_cycle: s.allow_update_within_cycle,
        week_start: s.week_start,
        auto_greeting_by_time: s.auto_greeting_by_time,
        greeting_morning: s.greeting_morning,
        greeting_afternoon: s.greeting_afternoon,
        greeting_evening: s.greeting_evening,
        greeting_night: s.greeting_night,
        greet_with_name_template: s.greet_with_name_template,
        greet_without_name_template: s.greet_without_name_template,
        choose_questionnaire_intro: s.choose_questionnaire_intro,
        closing_message: s.closing_message,
        already_collected_message: s.already_collected_message,
        no_open_cycle_message: s.no_open_cycle_message,
        no_questionnaires_message: s.no_questionnaires_message,
        no_questions_message: s.no_questions_message,
        dashboard_sum_question: s.dashboard_sum_question,
        after_action: s.after_action,
        after_extension: s.after_extension,
        entry_menu: s.entry_menu
      };
      await fetchJson('/api/settings', { method: 'PATCH', body: JSON.stringify(payload) });
      toast.success('נשמר');
    } catch (err: unknown) {
      const e = err as { message?: string };
      toast.error(e.message || 'שגיאה');
    } finally {
      setSaving(false);
    }
  }

  async function showFullToken() {
    if (showToken) {
      setShowToken(false);
      return;
    }
    const res = await fetchJson<{ token: string; webhookUrl: string }>('/api/settings/ivr-token');
    setTokenValue(res.token);
    setShowToken(true);
  }

  async function regenerateToken() {
    const ok = await confirm({
      title: 'יצירת טוקן IVR חדש',
      description: 'יצירת טוקן חדש תשבית את ה-IVR עד שתעדכן את ה-URL בימות המשיח. להמשיך?',
      confirmText: 'צור טוקן חדש',
      variant: 'destructive'
    });
    if (!ok) return;
    const res = await fetchJson<{ ivrToken: string; webhookUrl: string }>(
      '/api/settings/regenerate-ivr-token',
      { method: 'POST' }
    );
    setTokenValue(res.ivrToken);
    setShowToken(true);
    toast.success('נוצר טוקן חדש');
    refresh();
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => toast.success('הועתק'));
  }

  const sumQn = questionnaires.find((q) => q.id === s.dashboard_sum_question?.questionnaireId);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">הגדרות</h1>
        <p className="text-sm text-muted-foreground">תצורת הארגון והגדרות מערכת ה-IVR</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>קצב איסוף</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>מצב</Label>
            <Select value={s.cadence} onValueChange={(v) => update('cadence', v as Cadence)}>
              <SelectTrigger className="max-w-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CADENCE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    <div>
                      <div className="font-medium">{o.label}</div>
                      <div className="text-xs text-muted-foreground">{o.desc}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {s.cadence === 'weekly' && (
            <div className="space-y-2">
              <Label>תחילת שבוע</Label>
              <Select
                value={s.week_start}
                onValueChange={(v) => update('week_start', v as 'sunday' | 'monday')}
              >
                <SelectTrigger className="max-w-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sunday">ראשון</SelectItem>
                  <SelectItem value="monday">שני</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {s.cadence !== 'unlimited' && (
            <div className="flex items-center gap-2">
              <Switch
                checked={s.allow_update_within_cycle}
                onCheckedChange={(v) => update('allow_update_within_cycle', v)}
                id="allow_update"
              />
              <Label htmlFor="allow_update">אפשר ללקוח לעדכן רשומה קיימת באותו מחזור</Label>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>תבניות הודעות IVR</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border bg-muted/30 p-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={s.auto_greeting_by_time}
                onCheckedChange={(v) => update('auto_greeting_by_time', v)}
                id="auto_greeting"
              />
              <Label htmlFor="auto_greeting" className="font-medium">
                ברכה אוטומטית לפי שעות היום
              </Label>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              כאשר מופעל - הברכה תיבנה אוטומטית: ברכת התקופה + שם הלקוח (אם יש).
              <br />
              חלוקת השעות (Asia/Jerusalem): בוקר 5–11, צהריים 12–16, ערב 17–20, לילה 21–4.
            </p>
            {s.auto_greeting_by_time && (
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs">בוקר (5–11)</Label>
                  <Textarea
                    value={s.greeting_morning}
                    onChange={(e) => update('greeting_morning', e.target.value)}
                    rows={1}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">צהריים (12–16)</Label>
                  <Textarea
                    value={s.greeting_afternoon}
                    onChange={(e) => update('greeting_afternoon', e.target.value)}
                    rows={1}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">ערב (17–20)</Label>
                  <Textarea
                    value={s.greeting_evening}
                    onChange={(e) => update('greeting_evening', e.target.value)}
                    rows={1}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">לילה (21–4)</Label>
                  <Textarea
                    value={s.greeting_night}
                    onChange={(e) => update('greeting_night', e.target.value)}
                    rows={1}
                  />
                </div>
              </div>
            )}
          </div>

          {!s.auto_greeting_by_time && (
            <>
              <div className="space-y-1">
                <Label>ברכה עם שם</Label>
                <Textarea
                  value={s.greet_with_name_template}
                  onChange={(e) => update('greet_with_name_template', e.target.value)}
                  rows={2}
                />
                <p className="text-xs text-muted-foreground">
                  ניתן להשתמש ב-{'{name}'} עבור שם הלקוח
                </p>
              </div>
              <div className="space-y-1">
                <Label>ברכה ללא שם (לקוח חדש/ללא שם)</Label>
                <Textarea
                  value={s.greet_without_name_template}
                  onChange={(e) => update('greet_without_name_template', e.target.value)}
                  rows={2}
                />
              </div>
            </>
          )}
          <div className="space-y-1">
            <Label>פתיח לבחירת שאלון (כשיש כמה פעילים)</Label>
            <Textarea
              value={s.choose_questionnaire_intro}
              onChange={(e) => update('choose_questionnaire_intro', e.target.value)}
              rows={2}
            />
            <p className="text-xs text-muted-foreground">
              לאחריה תושמע אוטומטית רשימת השאלונים: "ל{'{ivrName1}'} הקש 1..."
            </p>
          </div>
          <div className="space-y-1">
            <Label>סיום מוצלח</Label>
            <Textarea
              value={s.closing_message}
              onChange={(e) => update('closing_message', e.target.value)}
              rows={2}
            />
          </div>
          <div className="space-y-1">
            <Label>כבר נקלטה רשומה למחזור</Label>
            <Textarea
              value={s.already_collected_message}
              onChange={(e) => update('already_collected_message', e.target.value)}
              rows={2}
            />
          </div>
          <div className="space-y-1">
            <Label>אין מחזור פתוח (manual בלבד)</Label>
            <Textarea
              value={s.no_open_cycle_message}
              onChange={(e) => update('no_open_cycle_message', e.target.value)}
              rows={2}
            />
          </div>
          <div className="space-y-1">
            <Label>אין שאלונים פעילים</Label>
            <Textarea
              value={s.no_questionnaires_message}
              onChange={(e) => update('no_questionnaires_message', e.target.value)}
              rows={2}
            />
          </div>
          <div className="space-y-1">
            <Label>שאלון ללא שאלות פעילות</Label>
            <Textarea
              value={s.no_questions_message}
              onChange={(e) => update('no_questions_message', e.target.value)}
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>גרף בלוח הבקרה</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            ניתן להציג סכום של שאלה מספרית (סוג tap) בנוסף לספירת רשומות.
          </p>
          <div className="grid grid-cols-1 gap-3 max-w-2xl sm:grid-cols-2">
            <div className="space-y-1">
              <Label>שאלון</Label>
              <Select
                value={s.dashboard_sum_question?.questionnaireId || 'none'}
                onValueChange={async (v) => {
                  if (v === 'none') {
                    update('dashboard_sum_question', null);
                    setQuestions([]);
                    return;
                  }
                  update('dashboard_sum_question', { questionnaireId: v, questionKey: '' });
                  const qs = await fetchJson<Question[]>(`/api/questionnaires/${v}/questions`);
                  setQuestions(qs);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">ללא</SelectItem>
                  {questionnaires.map((q) => (
                    <SelectItem key={q.id} value={q.id}>
                      {q.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {sumQn && (
              <div className="space-y-1">
                <Label>שאלה</Label>
                <Select
                  value={s.dashboard_sum_question?.questionKey || ''}
                  onValueChange={(v) =>
                    update('dashboard_sum_question', {
                      questionnaireId: s.dashboard_sum_question!.questionnaireId,
                      questionKey: v
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="בחר שאלה" />
                  </SelectTrigger>
                  <SelectContent>
                    {questions
                      .filter((q) => q.inputType === 'tap')
                      .map((q) => (
                        <SelectItem key={q.id} value={q.key}>
                          {q.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>כתובת webhook ל-IVR</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>כתובת מלאה</Label>
            <div className="flex gap-2">
              <Input value={s.webhookUrl} readOnly dir="ltr" className="font-mono text-xs" />
              <Button variant="outline" size="icon" onClick={() => copyToClipboard(s.webhookUrl)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              הטמע את הכתובת הזו ב"כתובת חיצונית" של השלוחה במערכת ימות המשיח.
            </p>
          </div>
          <Separator />
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label>קוד מלא לשלוחת API</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(buildYemotApiConfig(s.webhookUrl))}
              >
                <Copy className="h-4 w-4" />
                העתק קוד מלא
              </Button>
            </div>
            <Textarea
              value={buildYemotApiConfig(s.webhookUrl)}
              readOnly
              dir="ltr"
              rows={6}
              className="font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">
              באתר ניהול המערכת של ימות המשיח, בלשונית <strong>מערכת תוכן ← הגדרות מתקדמות</strong>,
              מחקו את כל המופיע והדביקו את הקוד הזה.
            </p>
          </div>
          <Separator />
          <div className="space-y-1">
            <Label>טוקן IVR</Label>
            <div className="flex gap-2">
              <Input
                value={showToken ? tokenValue || s.ivrTokenMasked : s.ivrTokenMasked}
                readOnly
                dir="ltr"
                className="font-mono text-xs"
              />
              <Button variant="outline" size="icon" onClick={showFullToken}>
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              {showToken && tokenValue && (
                <Button variant="outline" size="icon" onClick={() => copyToClipboard(tokenValue)}>
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={regenerateToken}>
              <RotateCw className="h-4 w-4" />
              צור טוקן חדש
            </Button>
            <p className="text-xs text-muted-foreground">
              יצירת טוקן חדש תשבית את ה-IVR עד שתעדכן את ה-URL בימות המשיח.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>יעד לאחר שאלון</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            לאן להעביר את השיחה לאחר סיום שאלון (וגם ביציאות מוקדמות כמו "כבר נקלטה רשומה"). שאלון יכול
            לדרוס הגדרה זו בעמוד עריכת השאלון.
          </p>
          <div className="grid grid-cols-1 gap-3 max-w-2xl sm:grid-cols-2">
            <div className="space-y-1">
              <Label>פעולה</Label>
              <Select
                value={s.after_action}
                onValueChange={(v) => update('after_action', v as AfterAction)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hangup">ניתוק השיחה</SelectItem>
                  <SelectItem value="main">חזרה לשלוחה ראשית</SelectItem>
                  <SelectItem value="extension">מעבר לשלוחה ספציפית</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {s.after_action === 'extension' && (
              <div className="space-y-1">
                <Label>מספר שלוחה</Label>
                <Input
                  value={s.after_extension}
                  onChange={(e) => update('after_extension', e.target.value)}
                  dir="ltr"
                  placeholder="למשל 5 או 1/2"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <EntryMenuEditor
        value={s.entry_menu}
        questionnaires={questionnaires}
        onChange={(menu) => update('entry_menu', menu)}
      />

      <GoogleSheetsCard />

      <OrgAdminsCard />

      <DownloadDataCard />

      <div className="flex justify-end gap-2 sticky bottom-4">
        <Button onClick={save} disabled={saving} size="lg">
          {saving ? 'שומר...' : 'שמור הגדרות'}
        </Button>
      </div>
    </div>
  );
}
